import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";

// Images
import header1 from "../assets/images/header1.jpg";
import header2 from "../assets/images/header2.jpg";
import header3 from "../assets/images/header3.jpg";

const headerImages = [header1, header2, header3];

function HeaderOne() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % headerImages.length);
    }, 5500);

    return () => clearInterval(timer);
  }, []);

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minWidth="100%"
      minHeight={{ xs: "78vh", md: "88vh" }}
      sx={{
        marginTop: "-64px",
        pt: { xs: 14, md: 12 },
        pb: { xs: 8, md: 8 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background Slides with Cross-Fade Transition */}
      {headerImages.map((image, index) => (
        <Box
          key={index}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `linear-gradient(to bottom, rgba(20, 14, 10, 0.45) 0%, rgba(20, 14, 10, 0.65) 100%), url(${image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: index === currentImageIndex ? 1 : 0,
            transition: "opacity 1.5s ease-in-out, transform 7s ease-out",
            transform: index === currentImageIndex ? "scale(1.03)" : "scale(1)",
            zIndex: 0,
          }}
        />
      ))}

      {/* Content */}
      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            mx: "auto",
          }}
        >
          {/* Modern Pill Badge */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2.2,
              py: 0.7,
              borderRadius: "30px",
              backgroundColor: "rgba(255, 255, 255, 0.12)",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(212, 175, 55, 0.35)",
              color: "#F3E3BE",
              fontSize: "0.76rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              mb: 3,
            }}
          >
            <span>✦</span> Masaje Oriental en Barcelona
          </Box>

          <Typography
            variant="h1"
            component="h1"
            sx={{
              color: "#FFFFFF",
              fontWeight: 700,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: { xs: "2rem", sm: "2.7rem", md: "3.25rem" },
              lineHeight: 1.18,
              letterSpacing: "-0.02em",
              textShadow: "0 4px 20px rgba(0, 0, 0, 0.4)",
              mb: 2.5,
              maxWidth: "840px",
            }}
          >
            Centro de Masajes en Barcelona | Masajes Orientales & Relajantes
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "rgba(255, 255, 255, 0.88)",
              fontSize: { xs: "0.95rem", sm: "1.08rem" },
              lineHeight: 1.7,
              maxWidth: "600px",
              mb: 4.5,
              textShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
            }}
          >
            Sumérgete en un oasis de serenidad, donde las técnicas milenarias asiáticas y la atención personalizada te brindarán un descanso absoluto.
          </Typography>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center" }}>
            <Button
              href="https://wa.link/kffk9v"
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              size="large"
              sx={{
                background: "linear-gradient(135deg, #DFBA73 0%, #C5A059 50%, #9E7736 100%)",
                color: "#1C1612",
                fontWeight: 700,
                borderRadius: "30px",
                px: { xs: 3.5, sm: 4.5 },
                py: { xs: 1.4, sm: 1.6 },
                fontSize: { xs: "0.9rem", sm: "0.98rem" },
                boxShadow: "0 8px 25px rgba(0, 0, 0, 0.35)",
                textTransform: "none",
                letterSpacing: "0.01em",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "linear-gradient(135deg, #E6C787 0%, #B89248 50%, #8E6A26 100%)",
                  boxShadow: "0 10px 30px rgba(158, 119, 54, 0.45)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              Reservar Cita →
            </Button>

            <Button
              href="#tratamientos"
              variant="outlined"
              size="large"
              sx={{
                color: "#FFFFFF",
                borderColor: "rgba(255, 255, 255, 0.4)",
                backgroundColor: "rgba(255, 255, 255, 0.08)",
                backdropFilter: "blur(10px)",
                fontWeight: 600,
                borderRadius: "30px",
                px: { xs: 3, sm: 4 },
                py: { xs: 1.4, sm: 1.6 },
                fontSize: { xs: "0.9rem", sm: "0.98rem" },
                textTransform: "none",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.18)",
                  borderColor: "#FFFFFF",
                  transform: "translateY(-2px)",
                },
              }}
            >
              Ver Tratamientos
            </Button>
          </Box>

          {/* Slide Indicator Dots */}
          <Box sx={{ display: "flex", gap: 1.2, mt: 4, zIndex: 1, alignItems: "center" }}>
            {headerImages.map((_, index) => (
              <Box
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                aria-label={`Ir a la imagen ${index + 1}`}
                sx={{
                  width: index === currentImageIndex ? 26 : 8,
                  height: 8,
                  borderRadius: "4px",
                  backgroundColor: index === currentImageIndex ? "#DFBA73" : "rgba(255, 255, 255, 0.4)",
                  transition: "all 0.4s ease",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: index === currentImageIndex ? "#E6C787" : "rgba(255, 255, 255, 0.75)",
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default HeaderOne;